import {
  Banner,
  Box,
  Flex,
  FormattedDateTime,
  Icon,
  Link,
  Paragraph,
  TextField,
  TextLabel,
  Toggle,
  Tooltip,
  TooltipContent,
} from '@customerio/pluma-components/react';
import {
  MIN_SUPPORT_TEAM_ACCESS_PERIOD_IN_DAYS,
  MAX_SUPPORT_TEAM_ACCESS_PERIOD_IN_DAYS,
  type SupportFormModalData,
} from '../types';
import * as styles from './support-form-modal.css';

type SupportAccessConsentProps = {
  data: SupportFormModalData;
  consentToAccess: boolean;
  daysLimit: number;
  daysLimitError?: string;
  onConsentChange: (isChecked: boolean) => void;
  onDaysLimitChange: (days: number) => void;
};

/**
 * The account-access half of the support form: grant access, for how long, or an explanation of
 * who can.
 *
 * Which of the three it shows depends on both the account's current access state and the user's
 * own permission, and it lives apart from the modal so that decision tree does not compete for
 * room with the parts of the form everyone sees.
 */
export function SupportAccessConsent({
  data,
  consentToAccess,
  daysLimit,
  daysLimitError,
  onConsentChange,
  onDaysLimitChange,
}: SupportAccessConsentProps) {
  // Someone who cannot grant access still needs to know why support may stall without it.
  if (!data.hasEditPermission) {
    return data.supportTeamAccessEnabled ? null : <AccessConsentExplanation />;
  }

  if (data.supportTeamAccessEnabled) {
    return (
      <Banner
        variant="information"
        description={
          <>
            Support access is currently granted until{' '}
            <FormattedDateTime date={data.accessGrantedUntil ?? 0} format="full" />. You can review or revoke access at
            any time on the{' '}
            <Link href="/settings/privacy" isExternal>
              Privacy & Data
            </Link>{' '}
            page.
          </>
        }
      />
    );
  }

  return (
    <div>
      <Flex justifyContent="space-between" alignItems="center">
        <TextLabel id="consent-label" htmlFor="consent-toggle" display="inline-flex" gap="050" alignItems="center">
          Grant support access
          <Tooltip>
            <Icon name="help" size="sm" />
            <TooltipContent>
              <Paragraph color="inherit" mb="200">
                To help with issues, our customer support team may need access to your account. If you choose to provide
                access, only authorized team members will have access while they help with your requests.
              </Paragraph>
              <Paragraph color="inherit">
                You can review or revoke access at any time on the Privacy & Data page found in Account Settings.
              </Paragraph>
            </TooltipContent>
          </Tooltip>
        </TextLabel>
        <Toggle
          id="consent-toggle"
          ariaLabelledby="consent-label"
          size="sm"
          isChecked={consentToAccess}
          onChange={(event) => onConsentChange(event.currentTarget.checked)}
        />
      </Flex>

      {consentToAccess ? (
        <Box>
          <Flex alignItems="center" gap="150">
            <span>Allow access for</span>
            <TextField
              value={String(daysLimit)}
              onInput={(event) => onDaysLimitChange(Number(event.target.value))}
              name="daysLimit"
              error={daysLimitError}
              ariaLabel="Number of days to allow support team access"
              type="number"
              min={MIN_SUPPORT_TEAM_ACCESS_PERIOD_IN_DAYS}
              max={MAX_SUPPORT_TEAM_ACCESS_PERIOD_IN_DAYS}
              className={styles.daysLimitInput}
            />
            <span>days.</span>
          </Flex>
        </Box>
      ) : null}
    </div>
  );
}

function AccessConsentExplanation() {
  return (
    <Banner
      title="We might need access to your account to help you"
      description={
        <>
          Contact your account administrator to grant us access under{' '}
          <Flex as="span" alignItems="center" display="inline-flex">
            Settings
            <Icon name="chevron-right" size="sm" />
            Account Settings
            <Icon name="chevron-right" size="sm" />
            <Link href="/settings/privacy" isExternal>
              Privacy & Data
            </Link>
            .
          </Flex>
        </>
      }
    />
  );
}
